<template>
  <div>
    <Menu />

    <section>
      <div class="container-fluid" v-if="!loading">
        <div class="row justify-content-center">
          <div class="col-lg-7 text-center mb-3">
            <h1 class="mb-3">Luxury Developments</h1>
            <p>
              This curated collection highlights some of South Africa's most sought-after property developments and luxury residential projects. Explore premium estates, contemporary apartments, secure lifestyle communities and visionary mixed-use developments. From iconic architecture to thoughtfully planned environments, each development reflects innovation, quality and long-term value. Discover spaces designed not just for living, but for elevated modern lifestyles.
            </p>
          </div>
        </div>

        <div class="row justify-content-center">
          <div class="col-12 text-center" v-if="developments.length > 0">
            <h2 class="pt-4 pb-2">Explore Our Luxury Developments</h2>
          </div>

          <div v-if="developments.length === 0">
            <h2 class="pt-5 text-center">No developments found</h2>
          </div>

          <div id="top"></div>

          <div
            class="col-lg-8 col-md-6 mt-3 mb-4 pb-4"
            v-for="(item, index) in developments"
            :key="index"
          >
            <div class="banner-section lifestyle-block switch-bg-lgrey p-3 h-100 bord-radius">
              <div class="portfolio-thumbnail">
                <a :href="'/developments/' + getDevelopmentSlug(item)">
                  <img
                    :src="getDevelopmentImage(item)"
                    class="w-100 force-height"
                    :alt="getDevelopmentTitle(item)"
                  >
                </a>

                <div class="thumbnail-priceTag" v-if="hasPriceRange(item)">
                  From {{ formatCurrency(getDevelopmentLowPrice(item)) }}
                </div>

                <div class="thumbnail-priceTag" v-else>
                  POA
                </div>

                <div class="portfolio-type">
                  <div class="typeOne mx-1">
                    Development
                  </div>

                  <div class="typeOne mx-1" v-if="hasProperties(item)">
                    {{ getPropertiesCount(item) }} Properties
                  </div>
                </div>
              </div>

              <a
                :href="'/developments/' + getDevelopmentSlug(item)"
                class="text-decoration-none text-reset d-block"
              >
                <h3 class="text-start mt-3">
                  {{ getDevelopmentTitle(item) }}
                </h3>
              </a>

              <p class="text-start mb-2">
                {{ getDevelopmentExcerpt(item) }}
              </p>

              <a class="btn btn-primary" style="float: right;" :href="'/developments/' + getDevelopmentSlug(item)">READ MORE</a>

              <p class="text-start pb-3" v-if="hasPriceRange(item)">
                <strong>Price range:</strong>
                {{ formatCurrency(getDevelopmentLowPrice(item)) }}
                -
                {{ formatCurrency(getDevelopmentHighPrice(item)) }}
              </p>

            </div>
          </div>
        </div>
      </div>

      <div v-else>
        <center><div class="spinner"></div></center>
        <h2 class="text-center">Loading luxury developments</h2>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import axios from 'axios'
import Menu from '@/components/home/Menu.vue'
import Footer from '@/components/home/Footer.vue'

export default {
  components: { Menu, Footer },

  data() {
    return {
      developments: [],
      loading: true
    }
  },

  mounted() {
    this.fetchDevelopments()
  },

  methods: {
    async fetchDevelopments() {
    this.loading = true

    try {
      const response = await axios.get(
        'https://luxury-dev.chaseveritt.co.za/api/v3/fetch_all_developments.php',
        {
          headers: {
            Accept: 'application/json'
          }
        }
      )

      if (
        response.data &&
        response.data.success === true &&
        Array.isArray(response.data.developments)
      ) {

        this.developments = response.data.developments
          // only luxury developments
          .filter(item => {
            return item.meta && item.meta.site === 'luxury'
          })

          // only developments with cover images
          .filter(item => {
            const dev =
              item.development &&
              item.development.data &&
              item.development.data.development &&
              item.development.data.development[0]

            return dev && dev.cover_image
          })

          // clean property list
          .map(item => {

            const dev = item.development.data.development[0]

            if (Array.isArray(dev.properties)) {
              dev.properties = dev.properties.filter(property => {
                return property.image && property.image.trim() !== ''
              })
            }

            return item
          })

      } else {
        this.developments = []
      }

    } catch (error) {
      console.log(error)
      this.developments = []
    }

    this.loading = false
  },

    getDevelopmentRow(item) {
    if (!item) {
      return {}
    }

    if (
      item.development &&
      item.development.data &&
      item.development.data.development &&
      item.development.data.development[0]
    ) {
      return item.development.data.development[0]
    }

    return {}
  },

    getDevelopmentTitle(item) {
      const row = this.getDevelopmentRow(item)

      if (row.title && row.title !== '') {
        return row.title
      }

      return 'Luxury Development'
    },

    getDevelopmentSlug(item) {
      const row = this.getDevelopmentRow(item)

      if (row.slug && row.slug !== '') {
        return row.slug
      }

      if (row.id && row.id !== '') {
        return row.id
      }

      return this.slugify(this.getDevelopmentTitle(item))
    },

    getDevelopmentImage(item) {
      const BASE_URL = 'https://luxury-dev.chaseveritt.co.za/'

      const row = this.getDevelopmentRow(item)

      let image = ''

      if (row.cover_image && row.cover_image !== '') {
        image = row.cover_image
      } else if (
        row.image_gallery &&
        row.image_gallery.length &&
        row.image_gallery[0].url
      ) {
        image = row.image_gallery[0].url
      }

      if (image && image !== '') {
        return BASE_URL + image
      }

      return BASE_URL + 'developments-images/placeholder.jpg'
    },

    getDevelopmentExcerpt(item) {
      const row = this.getDevelopmentRow(item)

      if (!row.description) {
        return ''
      }

      const cleanText = String(row.description).replace(/(<([^>]+)>)/gi, '')

      if (cleanText.length <= 180) {
        return cleanText
      }

      return cleanText.substring(0, 180) + '...'
    },

    getDevelopmentLowPrice(item) {
      const row = this.getDevelopmentRow(item)

      if (row.low_price) {
        return row.low_price
      }

      return ''
    },

    getDevelopmentHighPrice(item) {
      const row = this.getDevelopmentRow(item)

      if (row.high_price) {
        return row.high_price
      }

      return ''
    },

    hasPriceRange(item) {
      const row = this.getDevelopmentRow(item)

      if (row.low_price && row.high_price) {
        return true
      }

      return false
    },

    hasProperties(item) {
      const row = this.getDevelopmentRow(item)

      if (row.properties && row.properties.length) {
        return true
      }

      return false
    },

    getPropertiesCount(item) {
      const row = this.getDevelopmentRow(item)

      if (row.properties && row.properties.length) {
        return row.properties.length
      }

      return 0
    },

    formatCurrency(value) {
      if (!value) {
        return ''
      }

      const numberValue = Number(String(value).replace(/[^0-9.-]+/g, ''))

      if (Number.isNaN(numberValue)) {
        return value
      }

      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        maximumFractionDigits: 0
      }).format(numberValue)
    },

    slugify(value) {
      if (!value) {
        return 'development'
      }

      return value
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
    }
  }
}
</script>

<style scoped>
.force-height {
  height: 400px;
  object-fit: cover;
}

.spinner {
  border: 8px solid rgba(0, 0, 0, 0.1);
  border-left: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1.2s linear infinite;
  margin-top: 80px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>